<template>
  <div
    class="bg-white border rounded p-3 d-flex align-items-center justify-content-between gap-3"
  >
    <!-- Left: Selection Summary -->
    <div class="d-flex align-items-center gap-4 flex-grow-1 overflow-hidden">
      <slot name="details">
        <!-- Blocks share one layout: label, value, then an optional note line under the value. -->
        <div class="d-flex align-items-start gap-3 text-nowrap">
          <!-- Technology & Amount -->
          <div class="d-flex flex-column amount-block">
            <span
              class="actionbar-label micro-label text-uppercase text-body-tertiary fw-semibold mb-1"
              >{{ t("dashboard.portfolio.footer.technology_amount") }}</span
            >
            <div class="d-flex align-items-center flex-wrap gap-2">
              <div class="d-flex gap-1">
                <span
                  v-for="tech in technologies"
                  :key="tech"
                  class="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle fw-bold"
                >
                  {{ translateTechnology(tech, t) }}
                </span>
              </div>
              <span class="actionbar-value text-body fs-7 fw-bolder">
                {{
                  t("dashboard.portfolio.footer.selected_mwh", {
                    mwh: formatNumberWithSpaces(selection.totalSelectedMwh),
                  })
                }}
              </span>
            </div>
            <span class="text-body-tertiary small">
              {{
                t("dashboard.portfolio.footer.of_available", {
                  mwh: formatNumberWithSpaces(availableMwh),
                })
              }}
            </span>
          </div>

          <!-- Rows -->
          <div class="vr"></div>
          <div class="d-flex flex-column">
            <span
              class="actionbar-label micro-label text-uppercase text-body-tertiary fw-semibold mb-1"
            >
              {{ t("dashboard.portfolio.footer.rows") }}
            </span>
            <div class="d-flex align-items-center gap-2">
              <span class="actionbar-value text-body fs-7 fw-bolder">
                {{
                  t(
                    "dashboard.portfolio.footer.rows_selected",
                    selection.selectedItems.length
                  )
                }}
              </span>
              <span
                v-if="selection.partialItemCount > 0"
                class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
              >
                {{
                  t(
                    "dashboard.portfolio.footer.rows_partial",
                    selection.partialItemCount
                  )
                }}
              </span>
            </div>
            <span v-if="rowsNote" class="text-body-tertiary small">
              {{ rowsNote }}
            </span>
          </div>

          <!-- Production Period -->
          <div class="vr"></div>
          <div class="d-flex flex-column period-block">
            <span
              class="actionbar-label micro-label text-uppercase text-body-tertiary fw-semibold mb-1"
              >{{ t("dashboard.portfolio.footer.production_period") }}</span
            >
            <span
              class="actionbar-value text-body fs-7 fw-bolder text-truncate"
              >{{ period }}</span
            >
          </div>

          <!-- Production Support -->
          <div class="vr"></div>
          <div class="d-flex flex-column">
            <span
              class="actionbar-label micro-label text-uppercase text-body-tertiary fw-semibold mb-1"
            >
              {{ t("dashboard.portfolio.footer.production_support") }}
            </span>
            <span class="actionbar-value text-body fs-7 fw-bolder">{{
              summarySupport
            }}</span>
          </div>
        </div>
      </slot>
    </div>

    <!-- Right: Action Control -->
    <div class="flex-shrink-0">
      <slot name="action"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { PortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { useSelectedPortfolioSummary } from "@/dashboard/inventory_management/common/useSelectedPortfolioSummary";
import { translateTechnology } from "@/utils/technologies";
import { compact, uniq } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Mounted only while something is selected, so the summaries need no empty state.
const props = defineProps<{
  selection: PortfolioSelection;
  /** MWh of the selectable rows among the currently filtered rows. */
  availableMwh: number;
  /** Selected rows the current filters hide; 0 on pages that deselect them. */
  hiddenRowsCount: number;
  showPartialHint: boolean;
}>();

const { t } = useI18n();
const { summarySupport } = useSelectedPortfolioSummary(props.selection);

// The footer formats technologies (sorted) and the period (MM/YYYY) for itself; the summary
// composable's versions keep the Send/Sell review format, so only its support summary is shared.
const technologies = computed(() =>
  uniq(
    compact(props.selection.selectedItems.map((item) => item.technology_type))
  ).sort()
);

// MM/YYYY, collapsed to one month when the selection spans only one.
const period = computed(() => {
  const months = props.selection.selectedItems
    .map((item) => item.production_month)
    .filter(Boolean)
    .sort();
  if (months.length === 0) return "-";

  const formatMonth = (month: string) => {
    const [year, monthOfYear] = month.split("-");
    return `${monthOfYear}/${year}`;
  };

  const first = months[0];
  const last = months[months.length - 1];
  if (first === last) return formatMonth(first);
  return `${formatMonth(first)} - ${formatMonth(last)}`;
});

// The hint only matters until a row is partial; the pill next to the value says the rest.
const rowsNote = computed(() =>
  [
    props.showPartialHint && props.selection.partialItemCount === 0
      ? t("dashboard.portfolio.footer.partial_hint")
      : null,
    props.hiddenRowsCount > 0
      ? t("dashboard.portfolio.footer.rows_hidden", props.hiddenRowsCount)
      : null,
  ]
    .filter(Boolean)
    .join(" · ")
);
</script>

<style scoped lang="scss">
.amount-block {
  min-width: 150px;
}

.period-block {
  min-width: 120px;
}

// A notch above the shared micro-label: these label 1.25rem values, not a dense control.
.actionbar-label {
  font-size: 0.7rem;
}

.actionbar-value {
  line-height: 1.25;
}
</style>
